/**
 * Connector Specification Validator
 *
 * Validates connector specifications and provides helpful error messages
 * for common issues that can occur in connector definitions.
 */

export interface ConnectorSpecIssue {
  type: 'error' | 'warning' | 'info';
  category: 'mapping' | 'jsonpath' | 'endpoint' | 'configuration' | 'data_structure';
  message: string;
  suggestion?: string;
  field?: string;
  severity: 'high' | 'medium' | 'low';
}

export interface ConnectorSpecValidationResult {
  isValid: boolean;
  issues: ConnectorSpecIssue[];
  canProceed: boolean; // Whether the connector can still be used with limitations
  fallbackData?: any; // Fallback data when possible
}

/**
 * Validate a connector specification and return issues found
 */
export function validateConnectorSpec(
  vendor: any,
  availableFeeds: any[],
  discoveryResults: any,
  processedEndpoints: any,
  templateVersionInfo?: { templateVersion: 'v1' | 'v2'; isV2Template: boolean },
): ConnectorSpecValidationResult {
  const issues: ConnectorSpecIssue[] = [];

  // Check for missing or invalid vendor information
  if (!vendor || !vendor.name) {
    issues.push({
      type: 'error',
      category: 'configuration',
      message: 'Unable to identify the data source type',
      suggestion: 'Try refreshing the page or contact support if the issue persists',
      severity: 'high',
    });
  }

  // Check for missing discovery endpoints
  const discoveryEndpoints = processedEndpoints?.discovery || [];
  if (discoveryEndpoints.length === 0) {
    issues.push({
      type: 'error',
      category: 'endpoint',
      message: 'Unable to discover available data sources',
      suggestion:
        'This data source may not support automatic discovery. Try manually configuring your data feeds.',
      severity: 'high',
    });
  }

  // Check for issues in available feeds
  if (availableFeeds && availableFeeds.length > 0) {
    // Check for JSONPath parsing failures in the feeds
    const feedsWithJsonPathFailures = availableFeeds.filter(
      (feed) =>
        feed.feed_name === null ||
        feed.feed_name === undefined ||
        feed.display_name === null ||
        feed.display_name === undefined,
    );

    // Check for primary key parsing failures specifically
    const feedsWithPrimaryKeyFailures = availableFeeds.filter(
      (feed) =>
        (feed.primary_key_selectors && feed.primary_key_selectors.primary_key === '') ||
        (feed.primary_keys && Array.isArray(feed.primary_keys) && feed.primary_keys.length === 0),
    );

    // Only check for JSONPath parsing failures on V2 templates
    if (templateVersionInfo?.isV2Template) {
      // Check for any other JSONPath parsing failures (feed_params, etc.)
      const feedsWithOtherJsonPathFailures = availableFeeds.filter((feed) => {
        // Check if feed_params has null/undefined values that should have been extracted
        if (feed.feed_params) {
          return Object.values(feed.feed_params).some(
            (value) => value === null || value === undefined,
          );
        }
        return false;
      });

      if (feedsWithJsonPathFailures.length > 0) {
        issues.push({
          type: 'warning',
          category: 'jsonpath',
          message: `Data processing issues detected: ${feedsWithJsonPathFailures.length} feeds may have incomplete information`,
          suggestion:
            'Some feeds may have missing or incorrect data due to format changes. We recommend refreshing the connection to ensure you get the most accurate feed information.',
          severity: 'high',
        });
      }

      // Add specific warning for primary key parsing failures
      if (feedsWithPrimaryKeyFailures.length > 0) {
        issues.push({
          type: 'warning',
          category: 'jsonpath',
          message: `Primary key extraction failed for ${feedsWithPrimaryKeyFailures.length} feeds`,
          suggestion:
            'The data structure for primary keys may have changed. Try refreshing the connection to get the latest primary key information.',
          severity: 'high',
        });
      }

      // Add warning for other JSONPath parsing failures
      if (feedsWithOtherJsonPathFailures.length > 0) {
        issues.push({
          type: 'warning',
          category: 'jsonpath',
          message: `Data extraction issues detected for ${feedsWithOtherJsonPathFailures.length} feeds`,
          suggestion:
            'Some feed configuration data could not be extracted properly. This may indicate changes in your data source structure or connector configuration issues. Contact support if the problem persists.',
          severity: 'high',
        });
      }
    }

    availableFeeds.forEach((feed, index) => {
      // Check for missing feed names
      if (!feed.feed_name && !feed.name && !feed.value) {
        issues.push({
          type: 'warning',
          category: 'data_structure',
          message: `Some data feeds could not be properly identified`,
          suggestion:
            'The system will automatically assign names to these feeds. You can rename them later if needed.',
          field: `feeds[${index}].name`,
          severity: 'medium',
        });
      }

      // Check for missing display names
      if (!feed.display_name && !feed.label) {
        issues.push({
          type: 'warning',
          category: 'data_structure',
          message: `Some feeds may not have descriptive names`,
          suggestion:
            'You can rename these feeds to make them easier to identify in your data flows.',
          field: `feeds[${index}].display_name`,
          severity: 'low',
        });
      }
    });
  } else {
    issues.push({
      type: 'error',
      category: 'data_structure',
      message: 'No data sources were found',
      suggestion:
        'Verify your connection credentials and ensure the data source is accessible. You may need to check your permissions or network connection.',
      severity: 'high',
    });
  }

  // Check for JSONPath parsing failures in discovery results
  if (discoveryResults?.discoveryErrors && discoveryResults.discoveryErrors.length > 0) {
    const jsonPathErrors = discoveryResults.discoveryErrors.filter((error) => {
      const errorMessage = error.message || error.toString();
      return (
        errorMessage.includes('Lexical error') ||
        errorMessage.includes('JSONPath') ||
        errorMessage.includes('Unrecognized text')
      );
    });

    if (jsonPathErrors.length > 0) {
      issues.push({
        type: 'warning',
        category: 'jsonpath',
        message: 'Data extraction configuration issues detected',
        suggestion:
          'The connector configuration may need attention from support. Some data fields could not be extracted properly due to format changes or configuration issues.',
        severity: 'high',
      });
    }
  }

  // Check for discovery errors
  if (discoveryResults?.discoveryErrors && discoveryResults.discoveryErrors.length > 0) {
    discoveryResults.discoveryErrors.forEach((error: any, index: number) => {
      const errorMessage = error.message || error.toString();

      // Categorize common errors
      if (errorMessage.includes('Lexical error') || errorMessage.includes('JSONPath')) {
        issues.push({
          type: 'error',
          category: 'jsonpath',
          message: 'Data extraction failed due to format changes',
          suggestion:
            'The data structure from your source may have changed, or there may be a connector configuration issue. Try refreshing the connection first. If the problem persists, contact support as the connector may need to be updated.',
          severity: 'high',
        });
      } else if (errorMessage.includes('authentication') || errorMessage.includes('unauthorized')) {
        issues.push({
          type: 'error',
          category: 'endpoint',
          message: 'Authentication failed when connecting to the data source',
          suggestion:
            'Please verify your credentials and ensure they have the necessary permissions to access the data.',
          severity: 'high',
        });
      } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
        issues.push({
          type: 'error',
          category: 'endpoint',
          message: 'Network connection failed',
          suggestion:
            'Check your internet connection and ensure the data source is accessible. You may need to check firewall settings or VPN configuration.',
          severity: 'high',
        });
      } else {
        issues.push({
          type: 'error',
          category: 'endpoint',
          message: `Connection failed: ${errorMessage}`,
          suggestion:
            'Please check your connection settings and try again. If the problem persists, contact support.',
          severity: 'medium',
        });
      }
    });
  }

  // Determine if we can proceed
  const hasHighSeverityErrors = issues.some(
    (issue) => issue.severity === 'high' && issue.type === 'error',
  );
  const canProceed = !hasHighSeverityErrors && availableFeeds && availableFeeds.length > 0;

  // Create fallback data if needed
  let fallbackData;
  if (!canProceed && availableFeeds && availableFeeds.length > 0) {
    // Try to create fallback feeds with default values
    fallbackData = availableFeeds.map((feed, index) => ({
      ...feed,
      feed_name: feed.feed_name || feed.name || feed.value || `feed_${index}`,
      display_name: feed.display_name || feed.label || feed.feed_name || `Feed ${index + 1}`,
      primary_keys: feed.primary_keys || [],
      allowed_reference_feeds: feed.allowed_reference_feeds || ['base_auto'],
      feed_params: feed.feed_params || {},
    }));
  }

  const result = {
    isValid: issues.length === 0,
    issues,
    canProceed,
    fallbackData,
  };

  return result;
}

/**
 * Generate user-friendly error messages from validation results
 */
export function generateUserFriendlyMessages(validationResult: ConnectorSpecValidationResult): {
  title: string;
  message: string;
  suggestions: string[];
  canRetry: boolean;
} {
  const { issues, canProceed } = validationResult;

  if (issues.length === 0) {
    return {
      title: 'Connection Successful',
      message: 'Your data source is properly configured and ready to use.',
      suggestions: [],
      canRetry: false,
    };
  }

  const highSeverityIssues = issues.filter((issue) => issue.severity === 'high');
  const mediumSeverityIssues = issues.filter((issue) => issue.severity === 'medium');
  const lowSeverityIssues = issues.filter((issue) => issue.severity === 'low');

  let title: string;
  let message: string;
  let canRetry = true;

  if (highSeverityIssues.length > 0) {
    title = 'Connection Issues Detected';
    message = '';
    canRetry = true;
  } else if (mediumSeverityIssues.length > 0) {
    title = 'Data Source Warnings';
    message = 'Your connection is working, but some data feeds may need attention.';
    canRetry = false;
  } else {
    title = 'Data Source Information';
    message =
      'Your connection is working well. The suggestions below can help optimize your data setup.';
    canRetry = false;
  }

  const suggestions = issues
    .filter((issue) => issue.suggestion)
    .map((issue) => issue.suggestion!)
    .filter((suggestion, index, array) => array.indexOf(suggestion) === index); // Remove duplicates

  return {
    title,
    message,
    suggestions,
    canRetry,
  };
}

/**
 * Check if a specific issue can be automatically fixed
 */
export function canAutoFixIssue(issue: ConnectorSpecIssue): boolean {
  // Issues that can be automatically fixed
  const autoFixableTypes = ['missing_feed_name', 'missing_display_name', 'missing_primary_keys'];

  return autoFixableTypes.includes(issue.type as any);
}

/**
 * Apply automatic fixes to connector data
 */
export function applyAutoFixes(availableFeeds: any[], issues: ConnectorSpecIssue[]): any[] {
  const autoFixableIssues = issues.filter(canAutoFixIssue);

  if (autoFixableIssues.length === 0) {
    return availableFeeds;
  }

  return availableFeeds.map((feed, index) => {
    const fixedFeed = { ...feed };

    // Fix missing feed names
    if (!fixedFeed.feed_name && !fixedFeed.name && !fixedFeed.value) {
      fixedFeed.feed_name = `feed_${index}`;
      fixedFeed.name = `feed_${index}`;
      fixedFeed.value = `feed_${index}`;
    }

    // Fix missing display names
    if (!fixedFeed.display_name && !fixedFeed.label) {
      fixedFeed.display_name = fixedFeed.feed_name || fixedFeed.name || `Feed ${index + 1}`;
      fixedFeed.label = fixedFeed.display_name;
    }

    // Fix missing primary keys
    if (
      !fixedFeed.primary_keys ||
      (Array.isArray(fixedFeed.primary_keys) && fixedFeed.primary_keys.length === 0)
    ) {
      fixedFeed.primary_keys = [];
    }

    // Fix missing allowed reference feeds
    if (!fixedFeed.allowed_reference_feeds || fixedFeed.allowed_reference_feeds.length === 0) {
      fixedFeed.allowed_reference_feeds = ['base_auto'];
    }

    // Fix missing feed params
    if (!fixedFeed.feed_params) {
      fixedFeed.feed_params = {};
    }

    return fixedFeed;
  });
}
